import { useNavigate } from '@tanstack/react-router';
import type { AdminUserBotStatus, AdminUserReferralFilter, AdminUserSort, CityDto } from '@upme/contracts';
import { Button } from '@upme/ui/components/button';
import { Checkbox } from '@upme/ui/components/checkbox';
import { Input } from '@upme/ui/components/input';
import { Label } from '@upme/ui/components/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@upme/ui/components/select';
import { useEffect, useState } from 'react';
import type { UsersSearch } from '../../../routes/_authenticated/users/route';

interface Props {
  cities: ReadonlyArray<CityDto>;
  value: UsersSearch;
}

const ANY = '__any__';

const BOT_STATUS_OPTIONS: { value: AdminUserBotStatus; label: string }[] = [
  { value: 'started', label: 'Start нажат' },
  { value: 'not_started', label: 'Без Start' },
  { value: 'blocked', label: 'Заблокировал бота' },
];

const REFERRAL_OPTIONS: { value: AdminUserReferralFilter; label: string }[] = [
  { value: 'with', label: 'По приглашению' },
  { value: 'without', label: 'Пришли сами' },
];

const SORT_OPTIONS: { value: AdminUserSort; label: string }[] = [
  { value: 'createdAt', label: 'По регистрации' },
  { value: 'lastSeenAt', label: 'По последнему визиту' },
];

export function UsersFilters({ cities, value }: Props) {
  const navigate = useNavigate();
  const [local, setLocal] = useState<UsersSearch>(value);

  // Синхронизация при навигации назад/вперёд.
  useEffect(() => {
    setLocal(value);
  }, [value]);

  const apply = (patch: Partial<UsersSearch>) => {
    const next = { ...local, ...patch };
    setLocal(next);
    navigate({
      to: '/users',
      search: () => {
        const out: Record<string, string | boolean | number> = {};
        for (const [key, v] of Object.entries(next)) {
          // Страница сбрасывается: со старым номером новый срез мог бы
          // открыться на несуществующей странице и показаться пустым.
          if (key === 'page') continue;
          if (v !== undefined && v !== '' && v !== null && v !== false) out[key] = v;
        }
        return out;
      },
    });
  };

  const reset = () => {
    setLocal({});
    navigate({ to: '/users', search: () => ({}) });
  };

  const hasAny = Object.entries(local).some(([key, v]) => key !== 'page' && v !== undefined && v !== '');

  return (
    <div className="border rounded-md p-4 mb-4 space-y-4">
      <div className="grid gap-4 grid-cols-1 md:grid-cols-3 lg:grid-cols-4">
        <div>
          <Label htmlFor="users-search" className="text-xs">
            Поиск
          </Label>
          <Input
            id="users-search"
            value={local.search ?? ''}
            placeholder="username, имя или telegram id"
            onChange={(e) => setLocal({ ...local, search: e.target.value || undefined })}
            onKeyDown={(e) => {
              if (e.key === 'Enter') apply({ search: local.search });
            }}
            onBlur={() => apply({ search: local.search })}
          />
        </div>

        <div>
          <Label className="text-xs">Город</Label>
          <Select
            value={local.cityId ?? ANY}
            onValueChange={(v) => apply({ cityId: v === ANY ? undefined : v })}
          >
            <SelectTrigger aria-label="Город">
              <SelectValue placeholder="Все" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY}>Все</SelectItem>
              {cities.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label className="text-xs">Бот</Label>
          <Select
            value={local.botStatus ?? ANY}
            onValueChange={(v) => apply({ botStatus: v === ANY ? undefined : (v as AdminUserBotStatus) })}
          >
            <SelectTrigger aria-label="Статус бота">
              <SelectValue placeholder="Любой" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY}>Любой</SelectItem>
              {BOT_STATUS_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label className="text-xs">Источник</Label>
          <Select
            value={local.referral ?? ANY}
            onValueChange={(v) => apply({ referral: v === ANY ? undefined : (v as AdminUserReferralFilter) })}
          >
            <SelectTrigger aria-label="Источник">
              <SelectValue placeholder="Все" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY}>Все</SelectItem>
              {REFERRAL_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label htmlFor="created-from" className="text-xs">
            Регистрация с
          </Label>
          <Input
            id="created-from"
            type="date"
            value={local.createdFrom ?? ''}
            onChange={(e) => setLocal({ ...local, createdFrom: e.target.value || undefined })}
            onBlur={() => apply({ createdFrom: local.createdFrom })}
          />
        </div>

        <div>
          <Label htmlFor="created-to" className="text-xs">
            Регистрация по
          </Label>
          <Input
            id="created-to"
            type="date"
            value={local.createdTo ?? ''}
            onChange={(e) => setLocal({ ...local, createdTo: e.target.value || undefined })}
            onBlur={() => apply({ createdTo: local.createdTo })}
          />
        </div>

        <div>
          <Label htmlFor="last-seen-from" className="text-xs">
            Визит с
          </Label>
          <Input
            id="last-seen-from"
            type="date"
            value={local.lastSeenFrom ?? ''}
            onChange={(e) => setLocal({ ...local, lastSeenFrom: e.target.value || undefined })}
            onBlur={() => apply({ lastSeenFrom: local.lastSeenFrom })}
          />
        </div>

        <div>
          <Label htmlFor="last-seen-to" className="text-xs">
            Визит по
          </Label>
          <Input
            id="last-seen-to"
            type="date"
            value={local.lastSeenTo ?? ''}
            onChange={(e) => setLocal({ ...local, lastSeenTo: e.target.value || undefined })}
            onBlur={() => apply({ lastSeenTo: local.lastSeenTo })}
          />
        </div>

        <div>
          <Label className="text-xs">Сортировка</Label>
          <Select
            value={local.sort ?? 'createdAt'}
            onValueChange={(v) => apply({ sort: v as AdminUserSort })}
          >
            <SelectTrigger aria-label="Сортировка">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SORT_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-6">
        <div className="flex items-center gap-2">
          <Checkbox
            id="only-superadmin"
            checked={local.adminRole === 'SUPERADMIN'}
            onCheckedChange={(checked) => apply({ adminRole: checked ? 'SUPERADMIN' : undefined })}
          />
          <Label htmlFor="only-superadmin" className="text-sm font-normal">
            Только SUPERADMIN
          </Label>
        </div>

        <div className="flex items-center gap-2">
          <Checkbox
            id="only-city-moderators"
            checked={local.adminRole === 'CITY_MODERATOR'}
            onCheckedChange={(checked) => apply({ adminRole: checked ? 'CITY_MODERATOR' : undefined })}
          />
          <Label htmlFor="only-city-moderators" className="text-sm font-normal">
            Только менеджеры городов
          </Label>
        </div>

        <div className="flex items-center gap-2">
          <Checkbox
            id="include-fakes"
            checked={local.includeFakes === true}
            onCheckedChange={(checked) => apply({ includeFakes: checked === true ? true : undefined })}
          />
          <Label htmlFor="include-fakes" className="text-sm font-normal">
            Показывать фейков
          </Label>
        </div>

        {local.referredById && (
          <span className="text-sm text-muted-foreground">Приглашённые выбранным пользователем</span>
        )}

        {hasAny && (
          <Button variant="ghost" size="sm" onClick={reset}>
            Сбросить фильтры
          </Button>
        )}
      </div>
    </div>
  );
}
